import { inject, Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { Evaluation } from '@program/shared/evaluation/evaluation.model';
import { DisplayConfiguration, PrintableIframe } from '@program/shared/evaluations-admin-all/printable-iframe-types';
import { scoreBuilder } from '@program/shared/evaluations-admin-all/helpers/score-builder';
import { calculateTotals } from '@program/shared/evaluations-admin-all/helpers/calculate-totals';

/**
 * Creates a custom iframe to print evaluation reports.
 */
@Injectable()
export class PrintEvaluationsService {
  private _translateService = inject(TranslateService);
  private readonly _columns = {};
  private readonly _pass = {};
  private readonly _fail = {};

  constructor() {
    this._columns = this._translateService.instant('program.configuration.evaluation-display.columns');
    this._pass = this._translateService.instant('program.evaluation.PASS');
    this._fail = this._translateService.instant('program.evaluation.FAIL');
  }

  renderPrintableIframe(data: PrintableIframe, type: 'evaluation' | 'criteria'): void {
    if (type === 'evaluation') {
      this._renderEvaluationPrintableIframe(data);
      return;
    }

    this._renderCriteriaPrintableIframe(data);
  }

  private _renderEvaluationPrintableIframe({ heading, subheading, title, display }: PrintableIframe): void {
    const iframe = this._createIframe();

    const config: DisplayConfiguration = {
      tableTitle: (evaluation: Evaluation[]) => `${this._translateService.instant('program.evaluation.evaluator')}: ${evaluation?.[0].editorName}`,
      headers: [
        this._columns['evaluation-date'],
        this._columns['criteria'],
        this._columns['score'],
        this._columns['comments'],
      ],
      content: (evaluation: Evaluation) => `
        <tr>
          <td>${evaluation.updated ? new Date(evaluation.updated).toLocaleString() : ''}</td>
          <td>${evaluation.evaluationConfig?.name || ''}</td>
          <td>${scoreBuilder(evaluation, this._pass, this._fail)}</td>
          <td>${evaluation.comments || ''}</td>
        </tr>
      `
    };

    let content = '';

    display.forEach((data, index) => {
      const { _heading, _title, _subheading } = this._buildHeaders(heading, title, subheading, index);

      content += this._buildReportSectionHtml(config, _heading, _subheading, _title, data, true);
    });

    this._print(iframe, this._buildPageHtml(content));
  }

  private _renderCriteriaPrintableIframe({ heading, subheading, title, display }: PrintableIframe): void {
    const iframe = this._createIframe();

    const config: DisplayConfiguration = {
      tableTitle: (evaluation: Evaluation[]) => `${this._translateService.instant('program.evaluation.criteria')}: ${evaluation?.[0].evaluationConfig?.name}`,
      headers: [
        this._columns['evaluation-date'],
        this._columns['evaluator'],
        this._columns['score'],
        this._columns['comments'],
      ],
      content: (evaluation: Evaluation) => `
        <tr>
          <td>${evaluation.updated ? new Date(evaluation.updated).toLocaleString() : ''}</td>
          <td>${evaluation.editorName || ''}</td>
          <td>${scoreBuilder(evaluation, this._pass, this._fail)}</td>
          <td>${evaluation.comments || ''}</td>
        </tr>
      `
    };

    let content = '';

    display.forEach((data, index) => {
      const { _heading, _title, _subheading } = this._buildHeaders(heading, title, subheading, index);

      content += this._buildReportSectionHtml(config, _heading, _subheading, _title, data);
    });

    this._print(iframe, this._buildPageHtml(content));
  }

  private _buildPageHtml(reportSection: string): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 12px; text-align: left; font-size: 12px; }
          th { background-color: #f2f2f2; font-weight: bold; }
        </style>
      </head>
        <body>
          ${reportSection}
        </body>
      </html>
    `;
  }

  private _buildReportSectionHtml(config: DisplayConfiguration, heading: string, subheading: string, title: string, display: Evaluation[], showTotalScore = false): string {
    let content = `
        <h2 style="font-size: 1.2rem;">${heading ?? ''}</h2>
        <p style="font-size: 0.7rem;">${subheading ?? ''}</p>
        <h4 style="font-size: 1rem;">${title ?? ''}</h4>
        <b>${config.tableTitle(display)}</b>
        <table>
          <thead>
            <tr>
              ${config.headers.map(header => `<th>${header}</th>`).join('')}
            </tr>
          </thead>
        <tbody>
    `;

    if (display && display.length) {
      display.forEach(evaluation => content += config.content(evaluation));
    }

    content += `
      </tbody>
    </table>
    ${showTotalScore ? this._buildScoreSection(display) : ''}`;

    return content;
  }

  private _createIframe(): HTMLIFrameElement {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    return iframe;
  }

  private _print(iframe: HTMLIFrameElement, content: string): void {
    iframe.contentWindow.document.open();
    iframe.contentWindow.document.write(content);
    iframe.contentWindow.document.close();

    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        document.body.removeChild(iframe);
      }, 250);
    };
  }

  private _buildScoreSection(evaluations: Evaluation[]): string {
    const totals = calculateTotals(evaluations);

    return `
      ${
        totals?.totalScore >= 0 ?
        `
          <div style="float: right; margin: 1rem 0;">
            <span>${this._translateService.instant('program.evaluation.total-score')}:</span>
            <span>${this._translateService.instant('program.evaluation.' + totals?.passFail)} ${totals?.totalScore}/100</span>
          </div>
        ` :
        ''
      }
    `;
  }

  private _buildHeaders(heading: string, title: string, subheading: string, index: number) {
    return {
      _heading: index === 0 ? heading : null,
      _title: index === 0 ? title : null,
      _subheading: index === 0 ? subheading : null
    };
  }
}
